import TextLogo from "logo/textLogo";
import {
    MouseEvent,
    ReactElement,
    ReactNode,
    Ref,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import "./Graph.scss";
import {
    calculateAxisTicks,
    calculateBounds,
    calculateDragDomain,
    isZoomed as isZoomedHelper,
    type BaseDataPoint
} from "./graphHelpers";

// Re-export BaseDataPoint for backward compatibility
export type { BaseDataPoint } from "./graphHelpers";

// Chart config
const CHART_HEIGHT = 300;
const X_AXIS_TICK_INTERVAL = 200;
const CHART_MARGIN = { top: 20, right: 20, left: 10, bottom: 20 };
const LINE_COLOR = "rgb(88, 172, 248)";
const WATERMARK_LOGO_WIDTH = 50;

// Visual styling constants
const GRID_STROKE_DASHARRAY = "3 3";
const X_AXIS_LABEL_OFFSET = -10;
const Y_AXIS_LABEL_ANGLE = -90;
const Y_AXIS_WIDTH = 80;
const LINE_STROKE_WIDTH = 2;
const DOT_RADIUS = 3;

export type YAxisConfig = {
    readonly label: string;
    readonly tickFormatter: (value: number) => string;
    readonly domain: [number, number];
};

type GraphProps<T extends BaseDataPoint> = {
    readonly data: T[];
    readonly yDataKey: keyof T & string;
    readonly xAxisLabel: string;
    readonly yAxisConfig: YAxisConfig;
    readonly chartRef?: Ref<HTMLDivElement>;
    readonly renderTooltip?: (props: {
        active?: boolean;
        payload?: ReadonlyArray<{ payload: T }>;
        selectedPoint: number | null;
    }) => ReactNode;
    readonly children?: ReactNode;
    readonly lineName?: string;
    readonly xAxisTickInterval?: number;
    readonly xDomain?: [number, number] | null;
    readonly onXDomainChange?: (domain: [number, number] | null) => void;
    readonly graphTitle?: string;
};

export function Graph<T extends BaseDataPoint>({
    data,
    yDataKey,
    xAxisLabel,
    yAxisConfig,
    chartRef,
    renderTooltip,
    children,
    lineName,
    xAxisTickInterval = X_AXIS_TICK_INTERVAL,
    xDomain: controlledXDomain,
    onXDomainChange,
    graphTitle
}: GraphProps<T>): ReactElement {
    const [selectedPoint, setSelectedPoint] = useState<number | null>(null);
    const hoveredXValueRef = useRef<number | null>(null);
    const [internalXDomain, setInternalXDomain] = useState<
        [number, number] | null
    >(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState<number | null>(null);
    const [dragStartDomain, setDragStartDomain] = useState<
        [number, number] | null
    >(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Calculate bounds from data
    const bounds = useMemo(() => calculateBounds(data), [data]);

    const effectiveXDomain = controlledXDomain ?? internalXDomain;

    const setDomain = useCallback(
        (domain: [number, number] | null) => {
            if (controlledXDomain === undefined) {
                setInternalXDomain(domain);
            }
            onXDomainChange?.(domain);
        },
        [controlledXDomain, onXDomainChange]
    );

    const xAxisDomain = useMemo<[number, number]>(() => {
        if (effectiveXDomain) {
            // Ensure minimum is never below 0
            return [Math.max(0, effectiveXDomain[0]), effectiveXDomain[1]];
        }
        // Ensure minimum is never below 0
        return [Math.max(0, bounds.min), bounds.max];
    }, [effectiveXDomain, bounds.min, bounds.max]);

    const isZoomed = useMemo(() => {
        return isZoomedHelper(effectiveXDomain, bounds);
    }, [effectiveXDomain, bounds]);

    const xAxisTicks = useMemo(
        () => calculateAxisTicks(xAxisDomain, xAxisTickInterval),
        [xAxisDomain, xAxisTickInterval]
    );

    // Calculate dynamic Y-axis domain based on visible data points when zoomed
    const yAxisDomain = useMemo<[number, number]>(() => {
        if (!isZoomed) {
            return yAxisConfig.domain;
        }

        // Filter data points within the current X domain
        const visibleData = data.filter(
            (point) =>
                point.xValue >= xAxisDomain[0] && point.xValue <= xAxisDomain[1]
        );

        if (visibleData.length === 0) {
            return yAxisConfig.domain;
        }

        // Get Y values from visible data
        const yValues = visibleData.map(
            (point) => point[yDataKey] as unknown as number
        );
        const minY = Math.min(...yValues);
        const maxY = Math.max(...yValues);

        // Add 5% padding to top and bottom for visual breathing room
        const range = maxY - minY;
        const padding = range * 0.05;

        // Ensure minimum is never below 0
        return [Math.max(0, minY - padding), maxY + padding];
    }, [isZoomed, data, xAxisDomain, yDataKey, yAxisConfig.domain]);

    const handleDotClick = useCallback(
        (_e: unknown, payload: unknown) => {
            const typedPayload = payload as { payload?: T };
            const xValue = typedPayload?.payload?.xValue;
            if (xValue !== undefined) {
                setSelectedPoint(xValue === selectedPoint ? null : xValue);
            }
        },
        [selectedPoint]
    );

    // Chart-level click handler - uses hovered point to expand click target
    const handleChartClick = useCallback(() => {
        const hoveredXValue = hoveredXValueRef.current;
        if (hoveredXValue !== null) {
            // If hovering a point, select/deselect it
            setSelectedPoint(
                hoveredXValue === selectedPoint ? null : hoveredXValue
            );
        } else if (selectedPoint !== null) {
            // If clicking empty space, deselect
            setSelectedPoint(null);
        }
    }, [selectedPoint]);

    const handleMouseLeave = useCallback(() => {
        hoveredXValueRef.current = null;
    }, []);

    const handleMouseDown = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            // Only allow dragging when zoomed in
            if (!isZoomed) {
                return;
            }

            // Only start drag on left mouse button
            if (e.button !== 0) {
                return;
            }

            e.preventDefault();
            setIsDragging(true);
            setDragStartX(e.clientX);
            setDragStartDomain([xAxisDomain[0], xAxisDomain[1]]);
        },
        [isZoomed, xAxisDomain]
    );

    const updateDomainFromDrag = useCallback(
        (clientX: number) => {
            if (dragStartX === null || dragStartDomain === null) {
                return;
            }

            const container = containerRef.current;
            if (!container) {
                return;
            }

            const newDomain = calculateDragDomain(
                dragStartDomain,
                dragStartX,
                clientX,
                container.clientWidth,
                bounds
            );
            setDomain(newDomain);
        },
        [dragStartX, dragStartDomain, bounds, setDomain]
    );

    const handleMouseMove = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            if (!isDragging) {
                return;
            }

            e.preventDefault();
            updateDomainFromDrag(e.clientX);
        },
        [isDragging, updateDomainFromDrag]
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        setDragStartX(null);
        setDragStartDomain(null);
    }, []);

    // Add document-level mouse listeners for smooth dragging
    useEffect(() => {
        if (!isDragging) {
            return;
        }

        const handleDocumentMouseMove = (e: globalThis.MouseEvent) => {
            e.preventDefault();
            updateDomainFromDrag(e.clientX);
        };

        const handleDocumentMouseUp = () => {
            setIsDragging(false);
            setDragStartX(null);
            setDragStartDomain(null);
        };

        document.addEventListener("mousemove", handleDocumentMouseMove);
        document.addEventListener("mouseup", handleDocumentMouseUp);

        return () => {
            document.removeEventListener("mousemove", handleDocumentMouseMove);
            document.removeEventListener("mouseup", handleDocumentMouseUp);
        };
    }, [isDragging, updateDomainFromDrag]);

    // Handle mouse move for tooltip (separate from drag)
    const handleChartMouseMove = useCallback(
        (state: { activeLabel?: unknown }) => {
            // Don't update hover when dragging
            if (isDragging) {
                return;
            }
            const activeLabel = state?.activeLabel;
            if (activeLabel === null || activeLabel === undefined) {
                return;
            }
            const asNumber =
                typeof activeLabel === "number"
                    ? activeLabel
                    : Number(activeLabel);
            if (!Number.isNaN(asNumber)) {
                hoveredXValueRef.current = asNumber;
            }
        },
        [isDragging]
    );

    // Combined ref callback to handle both containerRef and chartRef
    const setRefs = useCallback(
        (node: HTMLDivElement | null) => {
            // Set internal ref
            containerRef.current = node;

            // Set external ref if provided
            if (chartRef) {
                if (typeof chartRef === "function") {
                    // Callback ref
                    chartRef(node);
                } else {
                    // RefObject
                    (
                        chartRef as React.MutableRefObject<HTMLDivElement | null>
                    ).current = node;
                }
            }
        },
        [chartRef]
    );

    return (
        <div
            ref={setRefs}
            className={`Graph-container${
                isDragging
                    ? " Graph-container--dragging"
                    : isZoomed
                    ? " Graph-container--draggable"
                    : ""
            }`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
        >
            {graphTitle && <div className="Graph-title">{graphTitle}</div>}
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <LineChart
                    data={data}
                    margin={CHART_MARGIN}
                    onMouseMove={handleChartMouseMove}
                    onMouseLeave={handleMouseLeave}
                    onClick={handleChartClick}
                >
                    <CartesianGrid strokeDasharray={GRID_STROKE_DASHARRAY} />
                    <XAxis
                        dataKey="xValue"
                        label={{
                            value: xAxisLabel,
                            position: "insideBottom",
                            offset: X_AXIS_LABEL_OFFSET
                        }}
                        ticks={xAxisTicks}
                        domain={xAxisDomain}
                        type="number"
                        allowDataOverflow
                        scale="linear"
                    />
                    <YAxis
                        label={{
                            value: yAxisConfig.label,
                            angle: Y_AXIS_LABEL_ANGLE,
                            position: "insideLeft",
                            style: { textAnchor: "middle" }
                        }}
                        tickFormatter={yAxisConfig.tickFormatter}
                        width={Y_AXIS_WIDTH}
                        domain={yAxisDomain}
                        allowDataOverflow
                    />
                    {renderTooltip && (
                        <Tooltip
                            content={({ active, payload }) =>
                                renderTooltip({
                                    active,
                                    payload: payload as ReadonlyArray<{
                                        payload: T;
                                    }>,
                                    selectedPoint
                                })
                            }
                        />
                    )}
                    <Line
                        type="monotone"
                        dataKey={yDataKey}
                        stroke={LINE_COLOR}
                        strokeWidth={LINE_STROKE_WIDTH}
                        dot={{
                            fill: LINE_COLOR,
                            r: DOT_RADIUS,
                            onClick: handleDotClick
                        }}
                        activeDot={false}
                        name={lineName}
                    />
                    {children}
                </LineChart>
            </ResponsiveContainer>
            <div className="Graph-watermark">
                <TextLogo width={WATERMARK_LOGO_WIDTH} />
            </div>
        </div>
    );
}
